import React, { useContext, useEffect, useMemo, useState } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CallIcon from "@material-ui/icons/Call";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import SendIcon from "@material-ui/icons/Send";
import MessageIcon from "@material-ui/icons/Message";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import TimerIcon from "@material-ui/icons/Timer";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ThumbUpAltIcon from "@material-ui/icons/ThumbUpAlt";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import ThumbDownAltIcon from "@material-ui/icons/ThumbDownAlt";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import { ArrowDownward, ArrowUpward } from "@material-ui/icons";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import moment from "moment";
import { isArray, isEmpty } from "lodash";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  LinearProgress,
  Stack,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import GroupsIcon from "@mui/icons-material/Groups";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";

import MainContainer from "../../components/MainContainer";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";
import Filters from "./Filters";
import { ChartsDate } from "./ChartsDate";
import ForbiddenPage from "../../components/ForbiddenPage";

const defaultCounters = {
  avgSupportTime: 0,
  avgWaitTime: 0,
  supportFinished: 0,
  supportHappening: 0,
  supportPending: 0,
  supportGroups: 0,
  leads: 0,
  activeTickets: 0,
  passiveTickets: 0,
  tickets: 0,
  waitRating: 0,
  withoutRating: 0,
  withRating: 0,
  percRating: 0,
  npsPromotersPerc: 0,
  npsPassivePerc: 0,
  npsDetractorsPerc: 0,
  npsScore: 0,
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";

  return {
    container: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    mainPaper: {
      flex: 1,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStyles,
      background: "transparent",
      border: "none",
      boxShadow: "none",
    },
    heroCard: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 28,
      padding: theme.spacing(3.5),
      color: "#fff",
      background: isDark
        ? "linear-gradient(135deg, #0f172a 0%, #182845 45%, #1d4ed8 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #06b6d4 100%)",
      boxShadow: "0 30px 80px rgba(15, 23, 42, 0.28)",
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.5),
      },
      "&::before": {
        content: '""',
        position: "absolute",
        width: 260,
        height: 260,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.08)",
        top: -80,
        right: -70,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        width: 180,
        height: 180,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.06)",
        bottom: -90,
        left: -30,
      },
    },
    heroContent: {
      position: "relative",
      zIndex: 1,
    },
    heroEyebrow: {
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 1.8,
      fontWeight: 700,
      opacity: 0.8,
      marginBottom: theme.spacing(1),
    },
    heroTitle: {
      fontSize: "2rem",
      fontWeight: 800,
      lineHeight: 1.1,
      marginBottom: theme.spacing(1.2),
      [theme.breakpoints.down("sm")]: {
        fontSize: "1.5rem",
      },
    },
    heroSubtitle: {
      maxWidth: 760,
      color: "rgba(255,255,255,0.82)",
      lineHeight: 1.6,
      marginBottom: theme.spacing(2.5),
    },
    heroMetrics: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2.5),
    },
    heroMetric: {
      borderRadius: 20,
      padding: theme.spacing(1.8, 2),
      background: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.14)",
      backdropFilter: "blur(8px)",
    },
    heroMetricLabel: {
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 1,
      color: "rgba(255,255,255,0.7)",
      marginBottom: theme.spacing(0.6),
    },
    heroMetricValue: {
      fontSize: "1.5rem",
      fontWeight: 800,
      lineHeight: 1.1,
    },
    heroActionRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      flexWrap: "wrap",
      marginBottom: theme.spacing(2),
    },
    heroChip: {
      color: "#fff !important",
      background: "rgba(255,255,255,0.12) !important",
      border: "1px solid rgba(255,255,255,0.16)",
      fontWeight: 600,
    },
    heroButton: {
      borderRadius: 14,
      paddingInline: theme.spacing(2),
      fontWeight: 700,
      textTransform: "none",
      boxShadow: "none",
    },
    filterPanel: {
      borderRadius: 22,
      padding: theme.spacing(0.5),
      border: isDark ? "1px solid rgba(148,163,184,0.14)" : "1px solid rgba(15,23,42,0.08)",
      background: isDark ? "rgba(15,23,42,0.42)" : "rgba(255,255,255,0.82)",
      boxShadow: isDark
        ? "0 20px 50px rgba(2, 6, 23, 0.24)"
        : "0 16px 36px rgba(15, 23, 42, 0.08)",
      backdropFilter: "blur(10px)",
    },
    customFixedHeightPaperLg: {
      padding: theme.spacing(2.5),
      display: "flex",
      overflow: "visible",
      flexDirection: "column",
      height: "100%",
      background: "transparent",
      border: "none",
      boxShadow: "none",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.8),
      flexWrap: "wrap",
    },
    sectionTitle: {
      fontSize: "1.1rem",
      fontWeight: 800,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(0.4),
    },
    sectionDescription: {
      color: theme.palette.text.secondary,
      fontSize: 14,
    },
    softPanel: {
      borderRadius: 24,
      padding: theme.spacing(2.2),
      border: isDark ? "1px solid rgba(148,163,184,0.14)" : "1px solid rgba(15,23,42,0.08)",
      background: isDark ? "linear-gradient(180deg, rgba(15,23,42,0.76), rgba(15,23,42,0.9))" : "linear-gradient(180deg, #ffffff, #f8fbff)",
      boxShadow: isDark
        ? "0 22px 50px rgba(2, 6, 23, 0.22)"
        : "0 18px 36px rgba(15, 23, 42, 0.08)",
    },
    metricCard: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 24,
      padding: theme.spacing(2.1),
      height: "100%",
      border: isDark ? "1px solid rgba(148,163,184,0.14)" : "1px solid rgba(15,23,42,0.08)",
      background: isDark ? "rgba(15,23,42,0.88)" : "rgba(255,255,255,0.98)",
      boxShadow: isDark
        ? "0 18px 42px rgba(2, 6, 23, 0.22)"
        : "0 18px 36px rgba(15, 23, 42, 0.08)",
      transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: isDark
          ? "0 24px 56px rgba(2, 6, 23, 0.28)"
          : "0 22px 44px rgba(15, 23, 42, 0.12)",
      },
    },
    metricAccent: {
      position: "absolute",
      inset: 0,
      opacity: 0.12,
      pointerEvents: "none",
    },
    metricTopRow: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
    metricIconWrap: {
      width: 52,
      height: 52,
      borderRadius: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.16)",
      backdropFilter: "blur(8px)",
      flexShrink: 0,
      "& svg": {
        fontSize: 26,
      },
    },
    metricBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      borderRadius: 999,
      padding: "6px 10px",
      fontSize: 12,
      fontWeight: 700,
      alignSelf: "flex-start",
      background: isDark ? "rgba(30,41,59,0.9)" : "rgba(15,23,42,0.05)",
      color: theme.palette.text.secondary,
    },
    metricValue: {
      position: "relative",
      zIndex: 1,
      fontSize: "2rem",
      fontWeight: 800,
      color: theme.palette.text.primary,
      lineHeight: 1.1,
      marginBottom: theme.spacing(0.7),
    },
    metricLabel: {
      position: "relative",
      zIndex: 1,
      color: theme.palette.text.primary,
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
    },
    metricCaption: {
      position: "relative",
      zIndex: 1,
      color: theme.palette.text.secondary,
      fontSize: 13,
      lineHeight: 1.5,
    },
    progressTrack: {
      height: 10,
      borderRadius: 999,
      background: isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)",
      overflow: "hidden",
      marginTop: theme.spacing(1.5),
    },
    progressFill: {
      height: "100%",
      borderRadius: 999,
      transition: "width 0.35s ease",
    },
    ratingHighlight: {
      borderRadius: 24,
      padding: theme.spacing(2.4),
      background: isDark
        ? "linear-gradient(135deg, rgba(37,99,235,0.24), rgba(16,185,129,0.14))"
        : "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(16,185,129,0.08))",
      border: isDark ? "1px solid rgba(96,165,250,0.22)" : "1px solid rgba(59,130,246,0.16)",
    },
    highlightValue: {
      fontSize: "2.2rem",
      fontWeight: 800,
      lineHeight: 1,
      marginTop: theme.spacing(0.8),
      marginBottom: theme.spacing(0.8),
    },
    tableWrapper: {
      borderRadius: 24,
      overflow: "hidden",
      border: isDark ? "1px solid rgba(148,163,184,0.14)" : "1px solid rgba(15,23,42,0.08)",
      background: isDark ? "rgba(15,23,42,0.82)" : "#fff",
    },
    loadingBar: {
      borderRadius: 999,
      height: 6,
      marginTop: theme.spacing(1.5),
      backgroundColor: "rgba(255,255,255,0.2)",
      "& .MuiLinearProgress-bar": {
        borderRadius: 999,
      },
    },
  };
});

const clampPercent = (value) => {
  const numeric = Number(value || 0);
  if (numeric < 0) return 0;
  if (numeric > 100) return 100;
  return numeric;
};

const Dashboard = () => {
  useTheme();
  const classes = useStyles();
  const [counters, setCounters] = useState(defaultCounters);
  const [attendants, setAttendants] = useState([]);
  const [period] = useState(0);
  const [loading, setLoading] = useState(false);
  const { find } = useDashboard();

  const nowIni = moment().startOf("month").format("YYYY-MM-DD");
  const now = moment().format("YYYY-MM-DD");

  const [showFilter, setShowFilter] = useState(false);
  const [dateStartTicket, setDateStartTicket] = useState(nowIni);
  const [dateEndTicket, setDateEndTicket] = useState(now);
  const [queueTicket, setQueueTicket] = useState(false);
  const [fetchDataFilter, setFetchDataFilter] = useState(false);

  const { user } = useContext(AuthContext);

  const receivedMessagesFiltered = useMessages({
    fromMe: false,
    dateStart: dateStartTicket,
    dateEnd: dateEndTicket,
  }).count;
  const receivedMessagesTotal = useMessages({ fromMe: false }).count;
  const sentMessagesFiltered = useMessages({
    fromMe: true,
    dateStart: dateStartTicket,
    dateEnd: dateEndTicket,
  }).count;
  const sentMessagesTotal = useMessages({ fromMe: true }).count;

  useEffect(() => {
    let isMounted = true;

    async function firstLoad() {
      if (isMounted) {
        await fetchData();
      }
    }

    const timeoutId = setTimeout(() => {
      firstLoad();
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDataFilter]);

  async function fetchData() {
    setLoading(true);

    let params = {};

    if (period > 0) {
      params = { days: period };
    } else {
      if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid()) {
        params = {
          ...params,
          date_from: moment(dateStartTicket).format("YYYY-MM-DD"),
        };
      }

      if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid()) {
        params = {
          ...params,
          date_to: moment(dateEndTicket).format("YYYY-MM-DD"),
        };
      }
    }

    if (Object.keys(params).length === 0) {
      params = { days: 30 };
    }

    try {
      const data = await find(params);
      const safeCounters = data?.counters || defaultCounters;

      setCounters(safeCounters);
      setAttendants(isArray(data?.attendants) ? data.attendants : []);
    } catch (error) {
      toast.error("Erro ao carregar dados do dashboard");
      setCounters(defaultCounters);
      setAttendants([]);
    } finally {
      setLoading(false);
    }
  }

  const exportarGridParaExcel = () => {
    const table = document.getElementById("grid-attendants");

    if (!table) {
      toast.error("Tabela de atendentes não encontrada para exportação.");
      return;
    }

    const ws = XLSX.utils.table_to_sheet(table);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendentes");
    XLSX.writeFile(wb, "relatorio-de-atendentes.xlsx");
  };

  const onlineUsers = useMemo(
    () => attendants.filter((attendant) => attendant.online === true).length,
    [attendants]
  );

  const dateRangeLabel = useMemo(() => {
    const start = moment(dateStartTicket);
    const end = moment(dateEndTicket);

    if (!start.isValid() || !end.isValid()) {
      return "Período atual";
    }

    return `${start.format("DD/MM/YYYY")} • ${end.format("DD/MM/YYYY")}`;
  }, [dateEndTicket, dateStartTicket]);

  const ratingPercent = clampPercent(counters.percRating);
  const scorePercent = clampPercent(counters.npsScore);

  const metricCards = [
    {
      label: "Em atendimento",
      value: counters.supportHappening || 0,
      caption: "Chamados ativos em andamento agora.",
      badge: "Operação",
      icon: <CallIcon style={{ color: "#06b6d4" }} />,
      accent: "linear-gradient(135deg, rgba(6,182,212,0.28), rgba(14,165,233,0.06))",
    },
    {
      label: "Aguardando",
      value: counters.supportPending || 0,
      caption: "Fila aguardando distribuição ou resposta.",
      badge: "Fila",
      icon: <HourglassEmptyIcon style={{ color: "#f59e0b" }} />,
      accent: "linear-gradient(135deg, rgba(245,158,11,0.26), rgba(251,191,36,0.06))",
    },
    {
      label: "Finalizados",
      value: counters.supportFinished || 0,
      caption: "Atendimentos encerrados no recorte selecionado.",
      badge: "Entrega",
      icon: <CheckCircleIcon style={{ color: "#22c55e" }} />,
      accent: "linear-gradient(135deg, rgba(34,197,94,0.24), rgba(16,185,129,0.06))",
    },
    {
      label: "Grupos",
      value: counters.supportGroups || 0,
      caption: "Conversas de grupo acompanhadas pelo time.",
      badge: "Canais",
      icon: <GroupsIcon style={{ color: "#8b5cf6" }} />,
      accent: "linear-gradient(135deg, rgba(139,92,246,0.24), rgba(124,58,237,0.06))",
    },
    {
      label: "Atendentes ativos",
      value: `${onlineUsers}/${attendants.length}`,
      caption: "Quantidade online versus equipe cadastrada.",
      badge: "Equipe",
      icon: <RecordVoiceOverIcon style={{ color: "#ec4899" }} />,
      accent: "linear-gradient(135deg, rgba(236,72,153,0.24), rgba(244,114,182,0.06))",
    },
    {
      label: "Novos contatos",
      value: counters.leads || 0,
      caption: "Oportunidades novas captadas no período.",
      badge: "Crescimento",
      icon: <GroupAddIcon style={{ color: "#14b8a6" }} />,
      accent: "linear-gradient(135deg, rgba(20,184,166,0.24), rgba(45,212,191,0.06))",
    },
    {
      label: "Mensagens recebidas",
      value: `${receivedMessagesFiltered}/${receivedMessagesTotal}`,
      caption: "Volume do período comparado ao acumulado.",
      badge: "Entrada",
      icon: <MessageIcon style={{ color: "#3b82f6" }} />,
      accent: "linear-gradient(135deg, rgba(59,130,246,0.24), rgba(96,165,250,0.06))",
    },
    {
      label: "Mensagens enviadas",
      value: `${sentMessagesFiltered}/${sentMessagesTotal}`,
      caption: "Saídas do período comparadas ao total geral.",
      badge: "Saída",
      icon: <SendIcon style={{ color: "#10b981" }} />,
      accent: "linear-gradient(135deg, rgba(16,185,129,0.24), rgba(52,211,153,0.06))",
    },
    {
      label: "T.M. de atendimento",
      value: formatTime(counters.avgSupportTime),
      caption: "Tempo médio para resolver um atendimento.",
      badge: "Eficiência",
      icon: <AccessAlarmIcon style={{ color: "#f97316" }} />,
      accent: "linear-gradient(135deg, rgba(249,115,22,0.24), rgba(251,146,60,0.06))",
    },
    {
      label: "T.M. de espera",
      value: formatTime(counters.avgWaitTime),
      caption: "Tempo médio até o início da interação.",
      badge: "SLA",
      icon: <TimerIcon style={{ color: "#ef4444" }} />,
      accent: "linear-gradient(135deg, rgba(239,68,68,0.24), rgba(248,113,113,0.06))",
    },
    {
      label: "Tickets ativos",
      value: counters.activeTickets || 0,
      caption: "Demandas com postura mais responsiva da operação.",
      badge: "Ativos",
      icon: <ArrowUpward style={{ color: "#f97316" }} />,
      accent: "linear-gradient(135deg, rgba(249,115,22,0.24), rgba(251,146,60,0.06))",
    },
    {
      label: "Tickets passivos",
      value: counters.passiveTickets || 0,
      caption: "Demandas dependentes de retorno do cliente.",
      badge: "Passivos",
      icon: <ArrowDownward style={{ color: "#22c55e" }} />,
      accent: "linear-gradient(135deg, rgba(34,197,94,0.24), rgba(74,222,128,0.06))",
    },
  ];

  const npsCards = [
    {
      label: "Score NPS",
      value: `${scorePercent}%`,
      progress: scorePercent,
      caption: "Leitura consolidada de percepção da operação.",
      color: "linear-gradient(90deg, #2563eb, #06b6d4)",
      icon: <TrendingUpIcon style={{ color: "#2563eb" }} />,
    },
    {
      label: "Promotores",
      value: `${clampPercent(counters.npsPromotersPerc)}%`,
      progress: clampPercent(counters.npsPromotersPerc),
      caption: "Clientes inclinados a recomendar o atendimento.",
      color: "linear-gradient(90deg, #10b981, #34d399)",
      icon: <ThumbUpAltIcon style={{ color: "#10b981" }} />,
    },
    {
      label: "Neutros",
      value: `${clampPercent(counters.npsPassivePerc)}%`,
      progress: clampPercent(counters.npsPassivePerc),
      caption: "Experiência estável, com margem clara para evolução.",
      color: "linear-gradient(90deg, #f59e0b, #fbbf24)",
      icon: <RemoveCircleOutlineIcon style={{ color: "#f59e0b" }} />,
    },
    {
      label: "Detratores",
      value: `${clampPercent(counters.npsDetractorsPerc)}%`,
      progress: clampPercent(counters.npsDetractorsPerc),
      caption: "Clientes que merecem atenção imediata da gestão.",
      color: "linear-gradient(90deg, #ef4444, #f97316)",
      icon: <ThumbDownAltIcon style={{ color: "#ef4444" }} />,
    },
  ];

  const attendanceCards = [
    {
      label: "Total de atendimentos",
      value: counters.tickets || 0,
      caption: "Volume consolidado do período filtrado.",
      icon: <CallIcon style={{ color: "#06b6d4" }} />,
      accent: "linear-gradient(135deg, rgba(6,182,212,0.24), rgba(59,130,246,0.06))",
    },
    {
      label: "Aguardando avaliação",
      value: counters.waitRating || 0,
      caption: "Chamados concluídos ainda sem resposta de satisfação.",
      icon: <AutorenewIcon style={{ color: "#f59e0b" }} />,
      accent: "linear-gradient(135deg, rgba(245,158,11,0.24), rgba(251,191,36,0.06))",
    },
    {
      label: "Sem avaliação",
      value: counters.withoutRating || 0,
      caption: "Interações encerradas sem feedback capturado.",
      icon: <PriorityHighIcon style={{ color: "#ef4444" }} />,
      accent: "linear-gradient(135deg, rgba(239,68,68,0.24), rgba(248,113,113,0.06))",
    },
    {
      label: "Atendimentos avaliados",
      value: counters.withRating || 0,
      caption: "Base efetiva usada para percepção de qualidade.",
      icon: <CheckCircleIcon style={{ color: "#22c55e" }} />,
      accent: "linear-gradient(135deg, rgba(34,197,94,0.24), rgba(74,222,128,0.06))",
    },
  ];

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <Paper className={classes.mainPaper}>
        <Container
          maxWidth={false}
          className={classes.container}
          style={{ padding: "16px 8px", maxWidth: "100%", overflowX: "hidden" }}
        >
          <Stack spacing={3}>
            <Paper className={classes.heroCard} elevation={0}>
              <div className={classes.heroContent}>
                <div className={classes.heroActionRow}>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label="Gerência" className={classes.heroChip} />
                    <Chip label={dateRangeLabel} className={classes.heroChip} />
                    <Chip
                      label={`${counters.tickets || 0} atendimentos no período`}
                      className={classes.heroChip}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={() => setShowFilter(!showFilter)}
                      startIcon={!showFilter ? <FilterListIcon /> : <ClearIcon />}
                      className={classes.heroButton}
                      style={{ background: "#ffffff", color: "#0f172a" }}
                    >
                      {showFilter ? "Ocultar filtros" : "Exibir filtros"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={exportarGridParaExcel}
                      startIcon={<SaveAltIcon />}
                      className={classes.heroButton}
                      style={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.28)",
                        background: "rgba(255,255,255,0.08)",
                      }}
                    >
                      Exportar atendentes
                    </Button>
                  </Stack>
                </div>

                <Typography className={classes.heroEyebrow}>
                  Dashboard executivo
                </Typography>
                <Typography className={classes.heroTitle}>
                  
                </Typography>
                <Typography className={classes.heroSubtitle}>
                  Acompanhe a operação, satisfação, produtividade e volume de atendimento.
                </Typography>

                {loading && <LinearProgress className={classes.loadingBar} />}

                <div className={classes.heroMetrics}>
                  <div className={classes.heroMetric}>
                    <div className={classes.heroMetricLabel}>Eficiência</div>
                    <div className={classes.heroMetricValue}>{formatTime(counters.avgSupportTime)}</div>
                  </div>
                  <div className={classes.heroMetric}>
                    <div className={classes.heroMetricLabel}>NPS Atual</div>
                    <div className={classes.heroMetricValue}>{scorePercent}%</div>
                  </div>
                  <div className={classes.heroMetric}>
                    <div className={classes.heroMetricLabel}>Equipe online</div>
                    <div className={classes.heroMetricValue}>{onlineUsers}</div>
                  </div>
                  <div className={classes.heroMetric}>
                    <div className={classes.heroMetricLabel}>Mensagens do período</div>
                    <div className={classes.heroMetricValue}>{receivedMessagesFiltered}</div>
                  </div>
                </div>
              </div>
            </Paper>

            {showFilter && (
              <div className={classes.filterPanel}>
                <Filters
                  classes={classes}
                  setDateStartTicket={setDateStartTicket}
                  setDateEndTicket={setDateEndTicket}
                  dateStartTicket={dateStartTicket}
                  dateEndTicket={dateEndTicket}
                  setQueueTicket={setQueueTicket}
                  queueTicket={queueTicket}
                  fetchData={setFetchDataFilter}
                />
              </div>
            )}

            <section>
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>Visão geral da operação</Typography>
                  <Typography className={classes.sectionDescription}>
                    
                  </Typography>
                </div>
                <Chip label={`Atualizado para ${dateRangeLabel}`} variant="outlined" />
              </div>

              <Grid2 container spacing={2}>
                {metricCards.map((card) => (
                  <Grid2 xs={12} sm={6} md={4} xl={3} key={card.label}>
                    <Paper className={classes.metricCard} elevation={0}>
                      <div className={classes.metricAccent} style={{ background: card.accent }} />
                      <div className={classes.metricTopRow}>
                        <div className={classes.metricIconWrap}>{card.icon}</div>
                        <div className={classes.metricBadge}>{card.badge}</div>
                      </div>
                      <Typography className={classes.metricValue}>{card.value}</Typography>
                      <Typography className={classes.metricLabel}>{card.label}</Typography>
                      <Typography className={classes.metricCaption}>{card.caption}</Typography>
                    </Paper>
                  </Grid2>
                ))}
              </Grid2>
            </section>

            <Grid2 container spacing={2}>
              <Grid2 xs={12} lg={8}>
                <Paper className={classes.softPanel} elevation={0}>
                  <div className={classes.sectionHeader}>
                    <div>
                      <Typography className={classes.sectionTitle}>Pesquisa de satisfação</Typography>
                      <Typography className={classes.sectionDescription}>
                        Distribuição do NPS com destaque visual para ações rápidas da gestão.
                      </Typography>
                    </div>
                    <Chip label={`Índice de avaliação ${ratingPercent}%`} color="primary" variant="outlined" />
                  </div>

                  <Grid2 container spacing={2}>
                    {npsCards.map((card) => (
                      <Grid2 xs={12} sm={6} key={card.label}>
                        <Paper className={classes.metricCard} elevation={0}>
                          <div className={classes.metricTopRow}>
                            <div className={classes.metricIconWrap}>{card.icon}</div>
                            <div className={classes.metricBadge}>{card.label}</div>
                          </div>
                          <Typography className={classes.metricValue}>{card.value}</Typography>
                          <Typography className={classes.metricCaption}>{card.caption}</Typography>
                          <div className={classes.progressTrack}>
                            <div
                              className={classes.progressFill}
                              style={{ width: `${card.progress}%`, background: card.color }}
                            />
                          </div>
                        </Paper>
                      </Grid2>
                    ))}
                  </Grid2>
                </Paper>
              </Grid2>

              <Grid2 xs={12} lg={4}>
                <Paper className={`${classes.softPanel} ${classes.ratingHighlight}`} elevation={0}>
                  <Typography className={classes.sectionTitle}>Índice de avaliação</Typography>
                  <Typography className={classes.sectionDescription}>
                    Percentual de atendimentos que realmente geraram feedback do cliente.
                  </Typography>
                  <Typography className={classes.highlightValue}>{ratingPercent}%</Typography>
                  <Typography className={classes.metricCaption}>
                    {counters.withRating || 0} avaliados de {counters.tickets || 0} atendimentos totais.
                  </Typography>
                  <div className={classes.progressTrack} style={{ marginTop: 20 }}>
                    <div
                      className={classes.progressFill}
                      style={{
                        width: `${ratingPercent}%`,
                        background: "linear-gradient(90deg, #1d4ed8, #10b981)",
                      }}
                    />
                  </div>
                  <Divider style={{ margin: "20px 0", opacity: 0.5 }} />
                  <Stack spacing={1.2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">
                        Aguardando avaliação
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        {counters.waitRating || 0}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">
                        Sem avaliação
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        {counters.withoutRating || 0}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">
                        Score NPS
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        {scorePercent}%
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid2>
            </Grid2>

            <section>
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>Resumo de atendimentos</Typography>
                  <Typography className={classes.sectionDescription}>
                    Cards dedicados ao volume e ao funil de avaliação para apoiar decisões rápidas.
                  </Typography>
                </div>
                <Chip label="Cards com foco em conversão e acompanhamento" variant="outlined" />
              </div>

              <Grid2 container spacing={2}>
                {attendanceCards.map((card) => (
                  <Grid2 xs={12} sm={6} lg={3} key={card.label}>
                    <Paper className={classes.metricCard} elevation={0}>
                      <div className={classes.metricAccent} style={{ background: card.accent }} />
                      <div className={classes.metricTopRow}>
                        <div className={classes.metricIconWrap}>{card.icon}</div>
                        <div className={classes.metricBadge}>Indicador</div>
                      </div>
                      <Typography className={classes.metricValue}>{card.value}</Typography>
                      <Typography className={classes.metricLabel}>{card.label}</Typography>
                      <Typography className={classes.metricCaption}>{card.caption}</Typography>
                    </Paper>
                  </Grid2>
                ))}
              </Grid2>
            </section>

            <section>
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>Gráficos gerenciais</Typography>
                  <Typography className={classes.sectionDescription}>
                    Componentes visuais refinados para comparar produtividade por usuário e volume diário.
                  </Typography>
                </div>
                <Chip label="Leitura analítica" variant="outlined" />
              </div>

              <Grid2 container spacing={2}>
                <Grid2 xs={12} lg={6}>
                  <Paper className={classes.softPanel} elevation={0}>
                    <ChatsUser />
                  </Paper>
                </Grid2>
                <Grid2 xs={12} lg={6}>
                  <Paper className={classes.softPanel} elevation={0}>
                    <ChartsDate />
                  </Paper>
                </Grid2>
              </Grid2>
            </section>

            <section>
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>Performance dos atendentes</Typography>
                  <Typography className={classes.sectionDescription}>
                    Tabela revitalizada com status visual mais claro para liderança acompanhar produtividade e disponibilidade.
                  </Typography>
                </div>
                <Chip label={`${attendants.length} atendentes monitorados`} variant="outlined" />
              </div>

              <div className={classes.tableWrapper}>
                <TableAttendantsStatus attendants={attendants} loading={loading} />
              </div>
            </section>
          </Stack>
        </Container>
      </Paper>
    </MainContainer>
  );
};

function formatTime(minutes) {
  return moment().startOf("day").add(minutes || 0, "minutes").format("HH[h] mm[m]");
}

export default Dashboard;
